import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Briefcase, MapPin, DollarSign, LayoutDashboard,
  LogOut, Menu, X, ChevronRight, CheckCircle, XCircle,
  Clock, TrendingUp, BarChart3, FileText, Send, UserCog, Shield, ClipboardList,
  Activity, Award, MessageCircle, Archive, MessageSquare, Plus, Trash2, RefreshCw, Link2, Radio, Database, Ban, Star, Lightbulb,
  Megaphone, ClipboardCheck, Receipt, ExternalLink, Loader2, Search, UserCircle, Settings, Mail, UserPlus, ChevronDown, Wallet
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import NotificationBell from '../components/NotificationBell';
import NewMessageAlert from '../components/NewMessageAlert';
import WorkStartAlert from '../components/WorkStartAlert';
import ServerResourcesCard from '../components/ServerResourcesCard';

import AdminUsers from './admin/AdminUsers';
import AdminAppConfig from './admin/AdminAppConfig';
import AdminEmailLogs from './admin/AdminEmailLogs';
import AuditorRatingsPanel from './admin/AuditorRatingsPanel';
import TodayStats from './admin/TodayStats';
import JobsInProgress from './admin/JobsInProgress';
import PayrollOverview from './admin/PayrollOverview';
import CompletedMapsThisWeek from './admin/CompletedMapsThisWeek';
import AdminPayroll from './admin/AdminPayroll';
import AdminClientReport from './admin/AdminClientReport';
import AdminJobs from './admin/AdminJobs';
import AdminCreateJob from './admin/AdminCreateJob';
import AdminPayments from './admin/AdminPayments';
import AdminPayouts from './admin/AdminPayouts';
import AdminReporting from './admin/AdminReporting';
import AdminClientReporting from './admin/AdminClientReporting';
import AdminStaff from './admin/AdminStaff';
import AdminClients from './admin/AdminClients';
import AdminJobRequests from './admin/AdminJobRequests';
import AdminInvoices from './admin/AdminInvoices';
import AdminAuditLogs from './admin/AdminAuditLogs';
import AdminSupport from './admin/AdminSupport';
import AdminArchives from './admin/AdminArchives';
import AdminDeletedJobs from './admin/AdminDeletedJobs';
import AdminOpportunities from './admin/AdminOpportunities';
import AdminRatings from './admin/AdminRatings';
import AdminLinks from './admin/AdminLinks';
import AdminTrackingLinks from './admin/AdminTrackingLinks';
import AdminLiveMap from './admin/AdminLiveMap';
import AdminParcelData from './admin/AdminParcelData';
import AdminParcelCounty from './admin/AdminParcelCounty';
import AdminParcelDataV2 from './admin/AdminParcelDataV2';
import AdminParcelV2County from './admin/AdminParcelV2County';
import AdminParcelConverter from './admin/AdminParcelConverter';
import AdminDoNotDeliver from './admin/AdminDoNotDeliver';
import AdminTimeClock from './admin/AdminTimeClock';
import AdminMarkJob from './admin/AdminMarkJob';
import AdminMyWork from './admin/AdminMyWork';
import AdminMarketing from './admin/AdminMarketing';
import TranslateButton from '../components/TranslateButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// -----------------------------------------------------------------
// Sidebar "Last deployed" chip
// -----------------------------------------------------------------
// Hits /api/admin/build-info on mount and renders the current
// commit hash + a relative timestamp ("3h ago"). The environment
// label is color-coded: green for production, amber for preview —
// so an admin viewing two browser tabs side-by-side can spot a
// stale deploy at a glance.

// Request age helpers — flag worker job requests waiting more than 24 hours
const requestWaitHours = (requestedAt) => requestedAt ? (Date.now() - new Date(requestedAt).getTime()) / 36e5 : 0;
const formatWait = (h) => (h >= 24 ? `${Math.floor(h / 24)}d ${Math.round(h % 24)}h` : `${Math.round(h)}h`);

const _formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diffSec = Math.floor((Date.now() - then) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    const d = Math.floor(diffSec / 86400);
    return d === 1 ? '1 day ago' : `${d} days ago`;
  } catch { return ''; }
};

const BuildInfoChip = () => {
  const [info, setInfo] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/admin/build-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setInfo(res.data);
      } catch { /* silent — chip is purely informational */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;

  const isProd = info.environment === 'production';
  const envColor = isProd
    ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30'
    : 'bg-amber-500/20 text-amber-200 border-amber-400/30';
  const envLabel = info.environment === 'unknown'
    ? 'env: ?'
    : info.environment;
  const relative = _formatRelative(info.commit_date);

  return (
    <div
      className="mb-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-[11px] text-purple-200/90 leading-tight"
      title={
        info.commit_subject
          ? `${info.commit_subject}\n\n${info.commit_full || info.commit_short}\n${info.commit_date || ''}`
          : (info.commit_full || info.commit_short)
      }
      data-testid="build-info-chip"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10.5px] text-purple-100">
          {info.commit_short || 'unknown'}
        </span>
        <span className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${envColor}`}>
          {envLabel}
        </span>
      </div>
      {relative && (
        <div className="text-[10px] text-purple-300/80 mt-0.5">
          deployed {relative}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, getAuthHeader, isStaff, userType, loading, adminRole, canAccessJobs, canAccessPayments, canAccessPayouts, canAccessReporting, canAccessClientReports, canAccessStaff } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [supportChatCount, setSupportChatCount] = useState({ unread_messages: 0, active_chats: 0 });

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, getAuthHeader());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSupportChatCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/support/unread-count`, getAuthHeader());
      setSupportChatCount(response.data);
    } catch (error) {
      console.error('Error fetching support chat count:', error);
    }
  };

  useEffect(() => {
    if (!loading && !isStaff) {
      navigate('/admin/login');
    }
  }, [loading, isStaff, navigate]);

  useEffect(() => {
    if (isStaff) {
      fetchStats();
      fetchSupportChatCount();
      
      // Poll for new support messages every 60 seconds
      const interval = setInterval(fetchSupportChatCount, 60000);
      return () => clearInterval(interval);
    }
  }, [isStaff]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Role label helper
  const getRoleLabel = () => {
    switch (userType) {
      case 'admin': return 'Administrator';
      case 'manager': return 'Manager';
      case 'auditor': return 'Auditor';
      default: return 'Staff';
    }
  };

  const getRoleBadgeColor = () => {
    switch (userType) {
      case 'admin': return 'bg-purple-500';
      case 'manager': return 'bg-blue-500';
      case 'auditor': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Build nav items based on role
  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true, roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/users', icon: Users, label: 'Users', roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/live-map', icon: Radio, label: 'Live Map', roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/create-job', icon: Plus, label: 'Create Job', roles: ['admin', 'manager'] },
    { path: '/admin/job-requests', icon: ClipboardList, label: 'Job Requests', roles: ['admin', 'manager'] },
    { path: '/admin/invoices', icon: Receipt, label: 'Invoices', roles: ['admin'] },
    { path: '/admin/jobs', icon: Briefcase, label: 'Jobs Management', roles: ['admin', 'manager'] },
    { path: '/admin/links', icon: Link2, label: 'Approval Links', roles: ['admin', 'manager'] },
    { path: '/admin/tracking-links', icon: MapPin, label: 'Tracking Links', roles: ['admin', 'manager'] },
    { path: '/admin/parcel-data-v2', icon: Database, label: 'Parcel Data Mgmt', roles: ['admin'] },
    { path: '/admin/parcel-converter', icon: Database, label: 'Parcel Converter', roles: ['admin'] },
    { path: '/admin/legacy-parcel-data', icon: Database, label: 'Legacy Parcel Data', roles: ['admin'] },
    { path: '/admin/do-not-deliver', icon: Ban, label: 'Do Not Deliver', roles: ['admin', 'manager'] },
    { path: '/admin/archives', icon: Archive, label: 'Archives', roles: ['admin', 'manager'] },
    { path: '/admin/deleted-jobs', icon: Trash2, label: 'Deleted Jobs', roles: ['admin'] },
    // Opportunities is now surfaced as a tab inside Marketing — see
    // /admin/marketing → Opportunities. The legacy route below stays
    // mounted so existing links / bookmarks keep working, but it no
    // longer appears in the sidebar.
    { path: '/admin/payments', icon: Shield, label: 'Audit Sessions', roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/payouts', icon: Send, label: 'Payouts', roles: ['admin'] },
    { path: '/admin/payroll', icon: Wallet, label: 'Payroll', roles: ['admin'] },
    { path: '/admin/support', icon: MessageCircle, label: 'Support Chat', roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/reporting', icon: BarChart3, label: 'Profit and Loss', roles: ['admin'] },
    { path: '/admin/client-reports', icon: FileText, label: 'Reports', roles: ['admin', 'manager'] },
    { path: '/admin/ratings', icon: Star, label: 'Ratings', roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/staff', icon: UserCog, label: 'Staff Management', roles: ['admin'] },
    { path: '/admin/clients', icon: Briefcase, label: 'Clients', roles: ['admin', 'manager'] },
    { path: '/admin/audit-logs', icon: ClipboardList, label: 'Audit Logs', roles: ['admin'] },
    { path: '/admin/time-clock', icon: Clock, label: 'Time Clock', roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/my-work', icon: ClipboardCheck, label: 'My Work', roles: ['admin', 'manager', 'auditor'] },
    { path: '/admin/marketing', icon: Megaphone, label: 'Marketing', roles: ['admin', 'manager'] },
    { path: '/admin/email-logs', icon: Mail, label: 'Email Log', roles: ['admin', 'manager'] },
    { path: '/admin/app-config', icon: Settings, label: 'App Config', roles: ['admin'] },
  ].filter(item => item.roles.includes(userType));

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu size={24} />
        </button>
        <h1 className="font-bold text-gray-900">Admin Dashboard</h1>
        <div className="flex items-center gap-2">
          <NotificationBell />
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-purple-900 to-indigo-900 text-white transform transition-transform lg:translate-x-0 flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Header - Fixed */}
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Briefcase size={20} />
              </div>
              <div>
                <h2 className="font-bold">Local Flyer</h2>
                <p className="text-xs text-purple-200">Admin Panel <span className="text-purple-300/70">v3.8</span></p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 space-y-1 pb-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            const isSupportChat = item.path === '/admin/support';
            const hasUnreadChats = isSupportChat && supportChatCount.active_chats > 0;
            const isJobRequests = item.path === '/admin/job-requests';
            const pendingRequests = isJobRequests && stats?.job_requests?.pending > 0 ? stats.job_requests.pending : 0;
            // Show amber pill on Jobs Management when there are hidden
            // split/grid/(Remaining) children waiting for admin review.
            const isJobsMgmt = item.path === '/admin/jobs';
            const hiddenChildren = isJobsMgmt && stats?.hidden_children?.pending_review > 0
              ? stats.hidden_children.pending_review
              : 0;
            // Same amber-pill pattern for the Marketing nav — counts
            // spam_leads rows that haven't been reviewed yet.
            const isMarketing = item.path === '/admin/marketing';
            const spamUnreviewed = isMarketing && stats?.marketing?.spam_unreviewed > 0
              ? stats.marketing.spam_unreviewed
              : 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  active
                    ? 'bg-white/20 text-white'
                    : 'text-purple-200 hover:bg-white/10'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="relative">
                  <Icon size={20} />
                  {(hasUnreadChats || pendingRequests > 0 || hiddenChildren > 0 || spamUnreviewed > 0) && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                  )}
                </div>
                <span className="font-medium flex-1">{item.label}</span>
                {hasUnreadChats && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                    {supportChatCount.active_chats}
                  </span>
                )}
                {pendingRequests > 0 && (
                  <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                    {pendingRequests}
                  </span>
                )}
                {hiddenChildren > 0 && (
                  <span
                    className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center"
                    title={`${hiddenChildren} hidden grid/split/(Remaining) children waiting for review`}
                    data-testid="nav-jobs-hidden-count"
                  >
                    {hiddenChildren}
                  </span>
                )}
                {spamUnreviewed > 0 && (
                  <span
                    className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center"
                    title={`${spamUnreviewed} spam submissions waiting for review`}
                    data-testid="nav-marketing-spam-count"
                  >
                    {spamUnreviewed}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <div className="bg-white/10 rounded-lg p-4 mb-3">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm text-purple-200">Logged in as</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor()} text-white`}>
                {getRoleLabel()}
              </span>
            </div>
            <p className="font-medium truncate">{user?.name}</p>
          </div>
          <BuildInfoChip />
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-purple-200 hover:text-white w-full px-4 py-2"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Desktop Header */}
        <div className="hidden lg:flex bg-white border-b px-6 py-3 items-center justify-between sticky top-0 z-30">
          <h1 className="font-semibold text-gray-800">
            {location.pathname === '/admin' ? 'Dashboard Overview' : 
             location.pathname.includes('/users') ? 'User Management' :
             location.pathname.includes('/live-map') ? 'Live Map' :
             location.pathname.includes('/jobs') ? 'Job Management' :
             location.pathname.includes('/parcel-converter') ? 'Parcel Converter' :
             location.pathname.includes('/parcel-data-v2') ? 'Parcel Data Mgmt' :
             location.pathname.includes('/legacy-parcel-data') ? 'Legacy Parcel Data' :
             location.pathname.includes('/parcel-data') ? 'Parcel Data Management' :
             location.pathname.includes('/do-not-deliver') ? 'Do Not Deliver' :
             location.pathname.includes('/archives') ? 'Archives' :
             location.pathname.includes('/deleted-jobs') ? 'Deleted Jobs' :
             location.pathname.includes('/payments') ? 'Audit Sessions' :
             location.pathname.includes('/payouts') ? 'Payouts' :
             location.pathname.includes('/payroll') ? 'Payroll' :
             location.pathname.includes('/support') ? 'Support Chat' :
             location.pathname.includes('/reporting') ? 'Profit and Loss' :
             location.pathname.includes('/client-reports') ? 'Reports' :
             location.pathname.includes('/client-report') ? 'Client Report' :
             location.pathname.includes('/staff') ? 'Staff Management' :
             location.pathname.includes('/audit-logs') ? 'Audit Logs' :
             location.pathname.includes('/email-logs') ? 'Email Log' :
             location.pathname.includes('/time-clock') ? 'Time Clock' : 'Admin'}
          </h1>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-semibold text-sm">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <span className="text-sm text-gray-600">{user?.name || 'Admin'}</span>
            </div>
          </div>
        </div>
        
        {/* "You have a new message!" popup — all staff roles, all pages */}
        <NewMessageAlert />
        {/* "Worker started working!" popup + chime — all staff roles */}
        <WorkStartAlert />

        <Routes>
          <Route path="/" element={
            <DashboardHome stats={stats} fetchStats={fetchStats} userType={userType} />
          } />
          <Route path="/users/*" element={<AdminUsers />} />
          <Route path="/live-map/*" element={<AdminLiveMap />} />
          <Route path="/app-config" element={
            userType === 'admin' ? <AdminAppConfig /> : <Navigate to="/admin" replace />
          } />
          <Route path="/email-logs" element={
            ['admin', 'manager'].includes(userType) ? <AdminEmailLogs /> : <Navigate to="/admin" replace />
          } />
          <Route path="/jobs/:jobId/mark" element={
            ['admin', 'manager'].includes(userType) ? <AdminMarkJob /> : <Navigate to="/admin" replace />
          } />
          <Route path="/client-report/:jobId" element={<AdminClientReport />} />
          <Route path="/jobs/*" element={
            canAccessJobs ? <AdminJobs /> : <Navigate to="/admin" replace />
          } />
          <Route path="/create-job/*" element={
            canAccessJobs ? <AdminCreateJob /> : <Navigate to="/admin" replace />
          } />
          <Route path="/archives/*" element={
            canAccessJobs ? <AdminArchives /> : <Navigate to="/admin" replace />
          } />
          <Route path="/deleted-jobs/*" element={
            userType === 'admin' ? <AdminDeletedJobs /> : <Navigate to="/admin" replace />
          } />
          <Route path="/opportunities/*" element={
            canAccessJobs ? <AdminOpportunities /> : <Navigate to="/admin" replace />
          } />
          <Route path="/links/*" element={
            canAccessJobs ? <AdminLinks /> : <Navigate to="/admin" replace />
          } />
          <Route path="/tracking-links/*" element={
            canAccessJobs ? <AdminTrackingLinks /> : <Navigate to="/admin" replace />
          } />
          <Route path="/parcel-data-v2/county/:countyId" element={
            userType === 'admin' ? <AdminParcelV2County /> : <Navigate to="/admin" replace />
          } />
          <Route path="/parcel-data-v2/*" element={
            userType === 'admin' ? <AdminParcelDataV2 /> : <Navigate to="/admin" replace />
          } />
          <Route path="/parcel-converter/*" element={
            userType === 'admin' ? <AdminParcelConverter /> : <Navigate to="/admin" replace />
          } />
          <Route path="/legacy-parcel-data/county/:countyId" element={
            userType === 'admin' ? <AdminParcelCounty /> : <Navigate to="/admin" replace />
          } />
          <Route path="/legacy-parcel-data/*" element={
            userType === 'admin' ? <AdminParcelData /> : <Navigate to="/admin" replace />
          } />
          {/* Backwards-compat: old /parcel-data path → legacy page */}
          <Route path="/parcel-data/county/:countyId" element={
            userType === 'admin' ? <AdminParcelCounty /> : <Navigate to="/admin" replace />
          } />
          <Route path="/parcel-data/*" element={
            userType === 'admin' ? <AdminParcelData /> : <Navigate to="/admin" replace />
          } />
          <Route path="/do-not-deliver/*" element={
            canAccessJobs ? <AdminDoNotDeliver /> : <Navigate to="/admin" replace />
          } />
          <Route path="/payments/*" element={
            canAccessPayments ? <AdminPayments /> : <Navigate to="/admin" replace />
          } />
          <Route path="/payouts/*" element={
            canAccessPayouts ? <AdminPayouts /> : <Navigate to="/admin" replace />
          } />
          <Route path="/payroll/*" element={
            userType === 'admin' ? <AdminPayroll /> : <Navigate to="/admin" replace />
          } />
          <Route path="/support/*" element={<AdminSupport />} />
          <Route path="/reporting/*" element={
            canAccessReporting ? <AdminReporting /> : <Navigate to="/admin" replace />
          } />
          <Route path="/client-reports/*" element={
            canAccessClientReports ? <AdminClientReporting /> : <Navigate to="/admin" replace />
          } />
          <Route path="/ratings/*" element={
            ['admin', 'manager', 'auditor'].includes(userType) ? <AdminRatings /> : <Navigate to="/admin" replace />
          } />
          <Route path="/staff/*" element={
            canAccessStaff ? <AdminStaff /> : <Navigate to="/admin" replace />
          } />
          <Route path="/clients/*" element={<AdminClients />} />
          <Route path="/job-requests/*" element={
            // Auditor has no access to job requests per spec.
            (canAccessJobs && adminRole !== 'auditor')
              ? <AdminJobRequests />
              : <Navigate to="/admin" replace />
          } />
          <Route path="/invoices/*" element={
            // Hard admin-only gate. Managers + auditors are bounced
            // to the dashboard — they never see financial data.
            userType === 'admin' && adminRole === 'admin'
              ? <AdminInvoices />
              : <Navigate to="/admin" replace />
          } />
          <Route path="/audit-logs/*" element={
            canAccessStaff ? <AdminAuditLogs /> : <Navigate to="/admin" replace />
          } />
          <Route path="/time-clock/*" element={<AdminTimeClock />} />
          <Route path="/my-work/*" element={<AdminMyWork />} />
          <Route path="/marketing/*" element={
            (userType === 'admin' || userType === 'manager')
              ? <AdminMarketing />
              : <Navigate to="/admin" replace />
          } />
        </Routes>
      </main>
    </div>
  );
};

// Clock Widget for Dashboard
const ClockWidget = ({ getAuthHeader }) => {
  const [clockedIn, setClockedIn] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [session, setSession] = useState(null);
  const timerRef = useRef(null);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/admin/time-clock/status`, getAuthHeader());
        setClockedIn(res.data.clocked_in);
        setSession(res.data.session);
        if (res.data.clocked_in && res.data.session) {
          setElapsed(Math.floor((Date.now() - new Date(res.data.session.clock_in).getTime()) / 1000));
        }
      } catch (e) {}
    };
    check();
  }, []);

  useEffect(() => {
    if (clockedIn && session) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - new Date(session.clock_in).getTime()) / 1000));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [clockedIn, session]);

  const toggle = async () => {
    try {
      if (clockedIn) {
        await axios.post(`${API}/admin/time-clock/clock-out`, {}, getAuthHeader());
        setClockedIn(false); setSession(null); setElapsed(0);
        if (timerRef.current) clearInterval(timerRef.current);
        toast.success('Clocked out!');
      } else {
        const res = await axios.post(`${API}/admin/time-clock/clock-in`, {}, getAuthHeader());
        setClockedIn(true); setSession(res.data);
        toast.success('Clocked in!');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;

  return (
    <Card
      className={`p-4 border-0 shadow-sm cursor-pointer transition-all hover:shadow-md ${clockedIn ? 'bg-emerald-50 ring-2 ring-emerald-200' : 'hover:bg-gray-50'}`}
      onClick={toggle}
      data-testid="dashboard-clock-widget"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${clockedIn ? 'bg-emerald-100' : 'bg-indigo-100'}`}>
            <Clock size={20} className={clockedIn ? 'text-emerald-600' : 'text-indigo-600'} />
          </div>
          <div>
            <p className="font-medium text-gray-900">{clockedIn ? 'Clock Out' : 'Clock In'}</p>
            <p className="text-sm text-gray-500">
              {clockedIn
                ? <span className="font-mono text-emerald-600 font-semibold">{String(h).padStart(2,'0')}:{String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}</span>
                : 'Start tracking hours'}
            </p>
          </div>
        </div>
        <div className={`w-3 h-3 rounded-full ${clockedIn ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`} />
      </div>
    </Card>
  );
};

// Dashboard Home Component
const DashboardHome = ({ stats, fetchStats, userType }) => {
  const { getAuthHeader } = useAuth();
  const [reports, setReports] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [clearingCache, setClearingCache] = useState(false);
  // Admin → "Login as Client" picker state.
  // Admins (role=admin only) can pick a specific client from this dialog and
  // open the customer portal in a new tab pre-authed as that client. Used
  // for debugging customer-reported issues.
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [loadingClientList, setLoadingClientList] = useState(false);
  const [clientPickerSearch, setClientPickerSearch] = useState('');
  const [impersonatingId, setImpersonatingId] = useState(null);
  // Auditor overview — "X of Y rated today" summary for the Ratings card
  const [auditorRatingSummary, setAuditorRatingSummary] = useState(null);

  useEffect(() => {
    if (userType !== 'auditor') return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/ratings/today`, getAuthHeader());
        const workers = Array.isArray(res.data?.workers) ? res.data.workers : [];
        setAuditorRatingSummary({
          total: workers.length,
          rated: workers.filter((w) => w.today_rated).length,
          needsAddress: !!res.data?.auditor_needs_address,
        });
      } catch (e) {
        setAuditorRatingSummary({ total: 0, rated: 0, needsAddress: false });
      }
    })();
  }, [userType]);

  // Users Requesting Jobs — dashboard card + quick-approve dropdown (admin/manager)
  const [userRequests, setUserRequests] = useState([]);
  const [showUserRequests, setShowUserRequests] = useState(false);
  const [approvingRequestId, setApprovingRequestId] = useState(null);

  const fetchUserRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/user-job-requests`, getAuthHeader());
      setUserRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (userType === 'auditor') return;
    fetchUserRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const handleApproveUserRequest = async (req) => {
    setApprovingRequestId(req.id);
    const silent = silentRequestIds.has(req.id);
    try {
      const res = await axios.put(`${API}/admin/jobs/${req.id}/approve-request`, { silent }, getAuthHeader());
      toast.success(res.data?.message || `Approved ${req.requester_name}`);
      fetchUserRequests();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve request');
    } finally { setApprovingRequestId(null); }
  };

  const [denyingRequestId, setDenyingRequestId] = useState(null);
  // Notify Silently — per-request checkbox; approve/deny skips the worker's notification
  const [silentRequestIds, setSilentRequestIds] = useState(new Set());

  const toggleSilentRequest = (jobId) => {
    setSilentRequestIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };

  const handleDenyUserRequest = async (req) => {
    setDenyingRequestId(req.id);
    const silent = silentRequestIds.has(req.id);
    try {
      await axios.put(`${API}/admin/jobs/${req.id}/deny-request`, { silent }, getAuthHeader());
      toast.success(`Denied ${req.requester_name}'s request for "${req.title}"${silent ? ' (no notification sent)' : ''}`);
      fetchUserRequests();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deny request');
    } finally { setDenyingRequestId(null); }
  };

  const openClientPicker = async () => {
    setClientPickerOpen(true);
    setClientPickerSearch('');
    if (clientList.length === 0) {
      setLoadingClientList(true);
      try {
        const res = await axios.get(`${API}/admin/clients`, getAuthHeader());
        setClientList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        toast.error('Failed to load clients');
      } finally {
        setLoadingClientList(false);
      }
    }
  };

  const handleImpersonateClient = async (client) => {
    setImpersonatingId(client.id);
    try {
      const res = await axios.post(
        `${API}/admin/clients/${client.id}/impersonate`,
        {},
        getAuthHeader(),
      );
      const { access_token, user } = res.data || {};
      if (!access_token || !user) {
        throw new Error('Server returned an incomplete impersonation payload');
      }
      const userParam = encodeURIComponent(btoa(JSON.stringify(user)));
      const url = `/client/login?from=admin&impersonate_token=${encodeURIComponent(access_token)}&impersonate_user=${userParam}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      setClientPickerOpen(false);
      toast.success(`Opened client portal as ${user.name || user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Impersonation failed');
    } finally {
      setImpersonatingId(null);
    }
  };

  const filteredClients = (() => {
    const q = clientPickerSearch.trim().toLowerCase();
    if (!q) return clientList;
    return clientList.filter((c) => {
      const hay = `${c.name || ''} ${c.email || ''} ${c.company || ''}`.toLowerCase();
      return hay.includes(q);
    });
  })();

  useEffect(() => {
    if (userType === 'admin') {
      fetchReports();
    } else {
      setLoadingReports(false);
    }
  }, [userType]);

  const fetchReports = async () => {
    try {
      setLoadingReports(true);
      const response = await axios.get(`${API}/admin/reports`, getAuthHeader());
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Clear all web cache (admin only)
  const handleClearCache = async () => {
    if (userType !== 'admin') {
      toast.error('Only administrators can clear the cache');
      return;
    }

    setClearingCache(true);
    try {
      // Clear localStorage
      const keysToPreserve = ['token', 'userType', 'language']; // Preserve auth tokens
      const preservedData = {};
      keysToPreserve.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) preservedData[key] = value;
      });
      localStorage.clear();
      Object.entries(preservedData).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear all caches (Service Worker caches)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }

      toast.success('Web cache cleared successfully! The page will reload.');
      
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Failed to clear cache completely');
    } finally {
      setClearingCache(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (!stats) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const canSeeFinancials = userType === 'admin';

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <div className="flex items-center gap-2">
          {/* "Login as Client" — opens a picker dialog so the admin/manager
              can pick a specific client to impersonate, then opens the
              customer-facing portal in a new tab pre-authed as that client.
              Admin + manager — hidden for auditor. */}
          {['admin', 'manager'].includes(userType) && (
            <Button
              variant="outline"
              size="sm"
              onClick={openClientPicker}
              title="Open the client portal as a specific client (admin/manager)"
              data-testid="admin-client-portal-btn"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
            >
              <UserCircle size={14} className="mr-1.5" />
              Login as Client
            </Button>
          )}
          <TranslateButton />
        </div>
      </div>

      {/* Server Resource Indicator — visible to admins only so they can spot
          when the container is approaching its memory / disk ceiling
          (and ask Emergent Support to bump the tier) before silent SIGKILLs
          start dropping background parcel imports. */}
      {userType === 'admin' && (
        <div className="mb-6">
          <ServerResourcesCard />
        </div>
      )}

      {userType === 'auditor' ? (
        // Auditor overview — exactly two cards above Quick Actions:
        // Audit Sessions (read-only review queue count) and Ratings.
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link to="/admin/payments">
            <Card className="p-6 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full" data-testid="auditor-audit-sessions-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Shield size={24} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.sessions.pending_approval}</p>
                  <p className="text-sm text-gray-500">Audit Sessions</p>
                </div>
              </div>
              <Badge className="mt-3 bg-green-100 text-green-700">
                sessions to review
              </Badge>
            </Card>
          </Link>

          <Link to="/admin/ratings">
            <Card className="p-6 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full" data-testid="auditor-ratings-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Star size={24} className="text-amber-500 fill-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {auditorRatingSummary ? `${auditorRatingSummary.rated}/${auditorRatingSummary.total}` : '—'}
                  </p>
                  <p className="text-sm text-gray-500">Ratings</p>
                </div>
              </div>
              <Badge className="mt-3 bg-amber-100 text-amber-700">
                {auditorRatingSummary?.needsAddress ? 'home address needed' : 'rated today'}
              </Badge>
            </Card>
          </Link>
        </div>
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.users.total}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
          </div>
          {stats.users.pending > 0 && (
            <Badge className="mt-3 bg-yellow-100 text-yellow-700">
              {stats.users.pending} pending approval
            </Badge>
          )}
        </Card>

        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Briefcase size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.jobs.total}</p>
              <p className="text-sm text-gray-500">Total Jobs</p>
            </div>
          </div>
          <Badge className="mt-3 bg-green-100 text-green-700">
            {stats.jobs.open} open
          </Badge>
        </Card>

        <Card
          className={`p-6 border-0 shadow-sm cursor-pointer transition-shadow hover:shadow-md ${showUserRequests ? 'ring-2 ring-blue-400' : ''}`}
          onClick={() => setShowUserRequests(v => !v)}
          data-testid="user-job-requests-card"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
              <UserPlus size={24} className="text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-gray-900">{userRequests.length}</p>
              <p className="text-sm text-gray-500">Users Requesting Jobs</p>
            </div>
            <ChevronDown size={18} className={`text-gray-400 shrink-0 transition-transform ${showUserRequests ? 'rotate-180' : ''}`} />
          </div>
          {userRequests.length > 0 && (() => {
            const overdue = userRequests.filter(r => requestWaitHours(r.requested_at) > 24).length;
            return overdue > 0 ? (
              <Badge className="mt-3 bg-red-600 text-white animate-pulse hover:bg-red-600" data-testid="user-requests-overdue-badge">
                {overdue} waiting 24h+
              </Badge>
            ) : (
              <Badge className="mt-3 bg-blue-600 text-white animate-pulse hover:bg-blue-600">
                click to review &amp; approve
              </Badge>
            );
          })()}
        </Card>

        {userType !== 'auditor' && (
          <Card className="p-6 border-0 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Clock size={24} className="text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.sessions.pending_approval}</p>
                <p className="text-sm text-gray-500">Pending Sessions</p>
              </div>
            </div>
            {stats.sessions.approved > 0 && (
              <Badge className="mt-3 bg-purple-100 text-purple-700">
                {stats.sessions.approved} ready to pay
              </Badge>
            )}
          </Card>
        )}

        {userType !== 'auditor' && (
          <Card className="p-6 border-0 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <DollarSign size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">${stats.payments.total_paid.toFixed(2)}</p>
                <p className="text-sm text-gray-500">Total Paid Out</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Users Requesting Jobs — expandable quick-approve dropdown */}
      {showUserRequests && (
        <Card className="p-4 border-0 shadow-sm mb-8 -mt-4 ring-1 ring-blue-200" data-testid="user-job-requests-panel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <UserPlus size={16} className="text-sky-600" /> Users Requesting Jobs
            </h3>
            <Link
              to="/admin/jobs"
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
              data-testid="user-requests-open-jobs-link"
            >
              Open Jobs Management →
            </Link>
          </div>
          {userRequests.length === 0 ? (
            <p className="text-sm text-gray-400 py-3 text-center" data-testid="user-requests-empty">
              No pending user job requests.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {userRequests.map((r) => {
                const waitHrs = requestWaitHours(r.requested_at);
                const overdue = waitHrs > 24;
                return (
                <div
                  key={r.id}
                  className={`flex items-center justify-between gap-3 py-2.5 ${overdue ? 'bg-red-50 -mx-2 px-2 rounded-md' : ''}`}
                  data-testid={`user-request-row-${r.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {r.requester_name}
                      {r.requester_rating != null && (
                        <span className={`ml-2 text-xs font-semibold ${r.requester_rating <= 3 ? 'text-red-600' : 'text-amber-600'}`}>
                          {Number(r.requester_rating).toFixed(1)} ★
                        </span>
                      )}
                      {r.requester_rating != null && r.requester_rating <= 3 && (
                        <Badge className="ml-2 bg-red-600 text-white text-[10px] px-1.5 py-0 hover:bg-red-600" data-testid={`low-rating-chip-${r.id}`}>
                          LOW RATING
                        </Badge>
                      )}
                      {overdue && (
                        <Badge className="ml-2 bg-red-600 text-white text-[10px] px-1.5 py-0 animate-pulse hover:bg-red-600" data-testid={`overdue-chip-${r.id}`}>
                          WAITING {formatWait(waitHrs)}
                        </Badge>
                      )}
                    </p>
                    <p className={`text-xs truncate ${overdue ? 'text-red-500' : 'text-gray-500'}`}>
                      wants "{r.title}" · {r.deliveries} deliveries
                      {r.requested_at && ` · requested ${new Date(r.requested_at).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <label
                      className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
                      title="No notification is sent to the worker for this approval or denial"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={silentRequestIds.has(r.id)}
                        onChange={() => toggleSilentRequest(r.id)}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        data-testid={`notify-silently-checkbox-${r.id}`}
                      />
                      Notify Silently
                    </label>
                    <Button
                      size="sm"
                      onClick={() => handleApproveUserRequest(r)}
                      disabled={approvingRequestId === r.id || denyingRequestId === r.id}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-user-request-${r.id}`}
                    >
                      {approvingRequestId === r.id
                        ? <Loader2 size={14} className="mr-1 animate-spin" />
                        : <CheckCircle size={14} className="mr-1" />}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDenyUserRequest(r)}
                      disabled={approvingRequestId === r.id || denyingRequestId === r.id}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      data-testid={`deny-user-request-${r.id}`}
                      title="Deny this request — the worker is notified and the job stays open"
                    >
                      {denyingRequestId === r.id
                        ? <Loader2 size={14} className="mr-1 animate-spin" />
                        : <X size={14} className="mr-1" />}
                      Deny
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
      </>
      )}

      {/* Ratings — the auditor's primary daily task, front and center */}
      {userType === 'auditor' && <AuditorRatingsPanel />}

      {/* Quick Actions */}
      <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Clock-In Quick Action Widget */}
        <ClockWidget getAuthHeader={getAuthHeader} />
        <Link to="/admin/users?status=pending">
          <Card className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-yellow-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Review Users</p>
                  <p className="text-sm text-gray-500">{stats.users.pending} pending</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </div>
          </Card>
        </Link>

        {userType !== 'auditor' && (
        <Link to="/admin/payments?status=pending_approval">
          <Card className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <CheckCircle size={20} className="text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Approve Sessions</p>
                  <p className="text-sm text-gray-500">{stats.sessions.pending_approval} pending</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </div>
          </Card>
        </Link>
        )}

        {userType !== 'auditor' && (
        <Link to="/admin/jobs">
          <Card className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Briefcase size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Post New Job</p>
                  <p className="text-sm text-gray-500">Create delivery job</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </div>
          </Card>
        </Link>
        )}

        {userType !== 'auditor' && (
        <Link to="/admin/job-requests">
          <Card className={`p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${stats?.job_requests?.pending > 0 ? 'ring-1 ring-amber-200' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ClipboardList size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Job Requests</p>
                  <p className="text-sm text-gray-500">{stats?.job_requests?.pending > 0 ? `${stats.job_requests.pending} pending` : 'From clients'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {stats?.job_requests?.pending > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">{stats.job_requests.pending}</Badge>
                )}
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            </div>
          </Card>
        </Link>
        )}

        {/* Clear Cache - Admin Only */}
        {userType === 'admin' && (
          <Card 
            className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={handleClearCache}
            data-testid="clear-cache-btn"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  {clearingCache ? (
                    <RefreshCw size={20} className="text-red-600 animate-spin" />
                  ) : (
                    <Trash2 size={20} className="text-red-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">Clear Web Cache</p>
                  <p className="text-sm text-gray-500">{clearingCache ? 'Clearing...' : 'Reset browser data'}</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </div>
          </Card>
        )}
      </div>

      {/* Jobs by Status - Admin Only */}
      {canSeeFinancials && reports?.jobs_by_status && (
        <div className="mt-8">
          <Card className="p-6 border-0 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Jobs by Status</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{reports.jobs_by_status.open}</p>
                <p className="text-sm text-gray-500">Open</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{reports.jobs_by_status.assigned}</p>
                <p className="text-sm text-gray-500">Assigned</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{reports.jobs_by_status.in_progress}</p>
                <p className="text-sm text-gray-500">In Progress</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{reports.jobs_by_status.completed}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-600">{reports.jobs_by_status.paid}</p>
                <p className="text-sm text-gray-500">Paid</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Jobs in Progress (parent jobs only) */}
      <div className="mt-6">
        <JobsInProgress />
      </div>

      {/* Today's Stats */}
      <div className="mt-6">
        <TodayStats />
      </div>

      {/* Yesterday's Stats */}
      <div className="mt-6">
        <TodayStats daysAgo={1} title="Yesterday's Stats" emptyText="No deliveries were recorded yesterday." />
      </div>

      {/* This Weeks Completed Maps — financial data, full admin only */}
      {canSeeFinancials && (
        <div className="mt-6">
          <CompletedMapsThisWeek />
        </div>
      )}

      {/* Payroll Overview — financial data, full admin only */}
      {canSeeFinancials && (
        <div className="mt-6">
          <PayrollOverview />
        </div>
      )}

      {/* Top Performers & Recent Activity - Admin Only */}
      {canSeeFinancials && reports && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Top Performers */}
          {reports.top_performers && reports.top_performers.length > 0 && (
            <Card className="p-6 border-0 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Award size={20} className="text-yellow-600" />
                <h2 className="text-lg font-semibold text-gray-900">Top Performers</h2>
              </div>
              <div className="space-y-3">
                {reports.top_performers.slice(0, 5).map((performer, index) => (
                  <div key={performer.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="font-medium">{performer.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{performer.deliveries} deliveries</p>
                      <p className="text-sm text-green-600">${performer.earnings.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent Activity */}
          {reports.recent_activity && reports.recent_activity.length > 0 && (
            <Card className="p-6 border-0 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Activity size={20} className="text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              </div>
              <div className="space-y-3">
                {reports.recent_activity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{activity.user_name}</p>
                      <p className="text-sm text-gray-500">{activity.job_title}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={getStatusColor(activity.status)}>
                        {activity.status.replace('_', ' ')}
                      </Badge>
                      <p className="text-sm text-gray-500 mt-1">
                        {activity.deliveries} deliveries • ${activity.earnings}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Login-as-Client picker. Admin + manager — backend also enforces. */}
      <Dialog open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="login-as-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle size={20} className="text-indigo-600" />
              Login as Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900">
              You'll open the customer-facing portal in a new tab as the picked
              client. The session is short-lived (30 min) and every login-as
              is logged to the audit trail.
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={clientPickerSearch}
                onChange={(e) => setClientPickerSearch(e.target.value)}
                placeholder="Search by name, email, or company…"
                className="pl-9"
                data-testid="login-as-client-search"
              />
            </div>
            <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
              {loadingClientList ? (
                <div className="p-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Loading clients…
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-400">
                  {clientList.length === 0 ? 'No clients yet' : 'No clients match your search'}
                </div>
              ) : (
                filteredClients.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50"
                    data-testid={`login-as-client-row-${c.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-gray-900 truncate">{c.name || '(no name)'}</p>
                        {c.disabled && (
                          <Badge variant="outline" className="text-[10px] border-red-200 text-red-600">Disabled</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{c.email}</p>
                      {c.company && <p className="text-[11px] text-gray-400 truncate">{c.company}</p>}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleImpersonateClient(c)}
                      disabled={impersonatingId !== null || c.disabled}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                      data-testid={`login-as-client-btn-${c.id}`}
                    >
                      {impersonatingId === c.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <>
                          <ExternalLink size={13} className="mr-1.5" />
                          Login
                        </>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientPickerOpen(false)} data-testid="login-as-client-cancel">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
